import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.55, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

/* ------------------------------------------------------------------ tones */
const TONES = {
  light: { section: "bg-background", head: "text-foreground", body: "text-muted-foreground", eyebrow: "bg-primary/10 text-primary", glow: false },
  tint: { section: "bg-card", head: "text-foreground", body: "text-muted-foreground", eyebrow: "bg-primary/10 text-primary", glow: false },
  dark: { section: "bg-[#0b1220] text-white", head: "text-white", body: "text-white/70", eyebrow: "bg-white/[0.07] text-teal-200 ring-1 ring-white/10", glow: true },
  brand: { section: "bg-primary text-primary-foreground", head: "text-primary-foreground", body: "text-primary-foreground/85", eyebrow: "bg-white/15 text-white ring-1 ring-white/25", glow: false },
};

const Glow = () => <div className="pointer-events-none absolute -right-24 top-1/4 h-96 w-96 rounded-full bg-primary/25 blur-[110px]" />;

/** macOS-style browser frame — the same treatment the homepage uses for product UI. */
export const Frame = ({ children, url = "startupotter.com", dark = false, testId }) => (
  <div
    data-testid={testId}
    className={`overflow-hidden rounded-2xl border shadow-[0_24px_60px_-20px_rgba(16,24,40,0.32)] ${
      dark ? "border-white/10 bg-[#0b1220]" : "border-border bg-card"
    }`}
  >
    <div className={`flex items-center gap-2 border-b px-4 py-3 ${dark ? "border-white/10 bg-white/[0.03]" : "border-border bg-secondary/60"}`}>
      <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
      <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
      <span className="h-3 w-3 rounded-full bg-[#28c840]" />
      <div className={`ml-3 hidden flex-1 truncate rounded-md px-3 py-1 text-xs sm:block ${dark ? "bg-white/[0.06] text-white/50" : "bg-card text-muted-foreground"}`}>
        {url}
      </div>
    </div>
    {children}
  </div>
);

/** A real product screenshot inside the frame. `cropClass` (e.g. "h-48") crops to the top. */
export const Shot = ({ src, alt, url, dark, testId, cropClass, priority = false }) => (
  <Frame url={url} dark={dark} testId={testId}>
    {cropClass ? (
      <div className={`overflow-hidden ${cropClass}`}>
        <img src={src} alt={alt} loading={priority ? "eager" : "lazy"} className="block h-full w-full object-cover object-top" />
      </div>
    ) : (
      <img src={src} alt={alt} loading={priority ? "eager" : "lazy"} className="block w-full" />
    )}
  </Frame>
);

export const Eyebrow = ({ icon: Icon, children, tone = "light" }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-[600] uppercase tracking-[0.1em] ${TONES[tone].eyebrow}`}>
    {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
    {children}
  </span>
);

/* ------------------------------------------------------------------- hero */
export const FeatureHero = ({ eyebrow, icon, title, copy, ctaLabel, ctaTo, visual, back = true, testId = "feature-hero" }) => (
  <section
    className="relative overflow-hidden border-b border-border"
    style={{ background: "radial-gradient(1100px 460px at 12% -20%, hsl(182 60% 88% / 0.85), transparent 60%), linear-gradient(160deg, #E7F6F6 0%, #F6FAFA 55%, #FFFFFF 100%)" }}
    data-testid={testId}
  >
    {back ? (
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        <Link to="/features" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="feature-back-link-top">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All features
        </Link>
      </div>
    ) : null}
    <div className={`mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 lg:px-8 lg:py-20 ${visual ? "lg:grid-cols-2 lg:gap-16" : ""}`}>
      <div>
        {eyebrow ? <Reveal><Eyebrow icon={icon}>{eyebrow}</Eyebrow></Reveal> : null}
        <Reveal delay={0.05}>
          <h1 className="mt-5 max-w-2xl text-4xl font-[700] tracking-[-0.02em] text-foreground sm:text-5xl lg:leading-[1.08]">{title}</h1>
        </Reveal>
        {copy ? <Reveal delay={0.1}><p className="mt-6 max-w-xl text-base leading-8 text-muted-foreground">{copy}</p></Reveal> : null}
        {ctaLabel ? (
          <Reveal delay={0.15}>
            <Link to={ctaTo || "/register"} className="mt-8 inline-block">
              <Button size="lg" className="rounded-full px-8" data-testid={`${testId}-cta`}>
                {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </Link>
          </Reveal>
        ) : null}
      </div>
      {visual ? <Reveal delay={0.12} y={30}>{visual}</Reveal> : null}
    </div>
  </section>
);

/* -------------------------------------------------------------------- row */
/** Alternating text / visual row. `flip` puts the visual first; `tone` sets the band style. */
export const FeatureRow = ({ label, icon, title, copy, extra, visual, flip = false, tone = "light", testId }) => {
  const t = TONES[tone];
  return (
    <section className={`relative overflow-hidden ${t.section}`} data-testid={testId}>
      {t.glow ? <Glow /> : null}
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-14 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-20">
        <Reveal className={flip ? "lg:order-2" : "lg:order-1"}>
          <div>
            {label ? <Eyebrow icon={icon} tone={tone}>{label}</Eyebrow> : null}
            <h2 className={`mt-4 text-2xl font-[680] tracking-[-0.01em] sm:text-3xl ${t.head}`}>{title}</h2>
            {copy ? <p className={`mt-4 max-w-lg text-base leading-8 ${t.body}`}>{copy}</p> : null}
            {extra}
          </div>
        </Reveal>
        <Reveal delay={0.1} y={28} className={flip ? "lg:order-1" : "lg:order-2"}>{visual}</Reveal>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------- icon grid */
export const IconGrid = ({ title, intro, items, tone = "tint", columns = 3, testId }) => {
  const t = TONES[tone];
  const cols = columns === 4 ? "sm:grid-cols-2 lg:grid-cols-4" : columns === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";
  const card = tone === "dark" ? "border-white/10 bg-white/[0.04]" : tone === "brand" ? "border-white/20 bg-white/10" : "border-border bg-background";
  const chip = tone === "light" || tone === "tint" ? "bg-primary/10 text-primary" : "bg-white/15 text-white";
  return (
    <section className={`relative overflow-hidden ${t.section}`} data-testid={testId}>
      {t.glow ? <Glow /> : null}
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
        {title ? (
          <Reveal>
            <div className="max-w-2xl">
              <h2 className={`text-2xl font-[680] tracking-[-0.01em] sm:text-3xl ${t.head}`}>{title}</h2>
              {intro ? <p className={`mt-4 text-base leading-8 ${t.body}`}>{intro}</p> : null}
            </div>
          </Reveal>
        ) : null}
        <div className={`mt-10 grid grid-cols-1 gap-4 ${cols}`}>
          {items.map((it, i) => (
            <Reveal key={it.title} delay={Math.min(i * 0.06, 0.3)}>
              <div className={`group h-full rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${card}`}>
                <span className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${chip}`}>
                  <it.icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className={`mt-4 text-base font-[640] ${t.head}`}>{it.title}</h3>
                <p className={`mt-2 text-sm leading-6 ${t.body}`}>{it.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* -------------------------------------------------------------- photo band */
export const PhotoBand = ({ src, alt, kicker, title, copy, testId }) => (
  <section className="relative isolate overflow-hidden" data-testid={testId}>
    <img src={src} alt={alt} loading="lazy" className="absolute inset-0 -z-10 h-full w-full object-cover" />
    <div className="absolute inset-0 -z-10 bg-[linear-gradient(100deg,rgba(4,13,18,0.95)_0%,rgba(4,13,18,0.82)_42%,rgba(4,13,18,0.45)_100%)]" />
    <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
      <Reveal>
        <div className="max-w-xl">
          {kicker ? <span className="text-xs font-[700] uppercase tracking-[0.14em] text-teal-300">{kicker}</span> : null}
          <h2 className="mt-4 text-2xl font-[700] tracking-[-0.01em] text-white sm:text-3xl lg:text-4xl lg:leading-[1.15]">{title}</h2>
          {copy ? <p className="mt-5 text-base leading-8 text-white/80">{copy}</p> : null}
        </div>
      </Reveal>
    </div>
  </section>
);

/* --------------------------------------------------------------- stat band */
export const StatBand = ({ items, tone = "brand", testId }) => {
  const t = TONES[tone];
  return (
    <section className={`relative overflow-hidden ${t.section}`} data-testid={testId}>
      {t.glow ? <Glow /> : null}
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-14 sm:grid-cols-3 sm:px-6 lg:px-8">
        {items.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div>
              <div className={`text-3xl font-[700] sm:text-4xl ${t.head}`}>{s.value}</div>
              <div className={`mt-2 text-sm leading-6 ${t.body}`}>{s.label}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ close */
export const ClosingCta = ({ title, copy, secondary, ctaLabel, ctaTo, testId = "feature-closing" }) => (
  <section className="bg-background" data-testid={testId}>
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <Reveal y={26}>
        <div className="relative overflow-hidden rounded-[2rem] border border-border bg-card p-10 text-center shadow-[0_24px_60px_-32px_rgba(16,24,40,0.3)] lg:p-14">
          <div className="pointer-events-none absolute -left-20 -top-24 h-64 w-64 rounded-full bg-primary/10 blur-3xl" />
          <h2 className="relative text-2xl font-[700] tracking-[-0.01em] text-foreground sm:text-3xl">{title}</h2>
          {copy ? <p className="relative mx-auto mt-5 max-w-2xl text-base leading-8 text-muted-foreground">{copy}</p> : null}
          {secondary ? <p className="relative mx-auto mt-3 text-sm font-[600] text-primary">{secondary}</p> : null}
          <div className="relative mt-8">
            <Link to={ctaTo || "/register"}>
              <Button size="lg" className="rounded-full px-10" data-testid={`${testId}-cta`}>
                {ctaLabel} <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

export const Pill = ({ children, active = false }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-[560] ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground ring-1 ring-border"}`}>
    {children}
  </span>
);

/** Bottom navigation: back to the Features index + the next feature in the tour. */
export const FeatureFooterNav = ({ next, testId = "feature-footer-nav" }) => (
  <section className="border-t border-border bg-card" data-testid={testId}>
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1.6fr]">
        <Link
          to="/features"
          className="group flex items-center gap-3 rounded-2xl border border-border bg-background p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30"
          data-testid="feature-back-link-bottom"
        >
          <ArrowLeft className="h-5 w-5 text-primary transition-transform group-hover:-translate-x-0.5" aria-hidden="true" />
          <span>
            <span className="block text-[10px] font-[700] uppercase tracking-[0.12em] text-muted-foreground">Back</span>
            <span className="block text-base font-[640] text-foreground">All features</span>
          </span>
        </Link>
        <Link
          to={next.to}
          className="group flex items-center justify-between gap-4 rounded-2xl bg-primary p-6 text-primary-foreground transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary/90"
          data-testid="feature-next-link"
        >
          <span>
            <span className="block text-[10px] font-[700] uppercase tracking-[0.12em] text-primary-foreground/70">Up next</span>
            <span className="mt-0.5 block text-lg font-[680]">{next.label}</span>
            <span className="mt-1 block text-sm leading-6 text-primary-foreground/85">{next.desc}</span>
          </span>
          <ArrowRight className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);
